import { defineComponent } from 'vue';
import {
    QBtn, QCheckbox, QInput, QMenu, QSpace, QTooltip, QSeparator,
} from 'quasar';
import {
    filterVisibleMessages,
    addMutePattern,
    removeMutePattern,
    normalizeMessageLogPrefs,
    suggestMutePattern,
    formatMutePatternLabel,
} from './flowMessageLog.js';
import './flowMessageLog.css';

const SEVERITY_LABEL = {
    info: 'Info',
    warning: 'Warn',
    error: 'Error',
};

export default defineComponent({
    name: 'flow-v2-message-log',
    components: {
        QBtn, QCheckbox, QInput, QMenu, QSpace, QTooltip, QSeparator,
    },
    props: {
        messages: { type: Array, default: () => [] },
        prefs: { type: Object, required: true },
        mutedCount: { type: Number, default: 0 },
    },
    emits: ['update:prefs', 'dismiss', 'clear', 'clearVisible'],
    data() {
        return {
            filtersOpen: false,
            muteDraft: '',
        };
    },
    computed: {
        visibleMessages() {
            return filterVisibleMessages(this.messages, this.prefs);
        },
        hiddenByFilter() {
            return Math.max(0, (this.messages?.length || 0) - this.visibleMessages.length);
        },
        hasAnything() {
            return (this.messages?.length || 0) > 0 || this.mutedCount > 0;
        },
        titleText() {
            const n = this.visibleMessages.length;
            const total = this.messages?.length || 0;
            if (this.prefs.collapsed)
                return n === 1 ? '1 message' : `${n} messages`;
            if (n === total)
                return n === 1 ? '1 message' : `${n} messages`;
            return `${n} of ${total} messages`;
        },
        metaText() {
            const bits = [];
            if (this.hiddenByFilter)
                bits.push(`${this.hiddenByFilter} filtered`);
            if (this.mutedCount)
                bits.push(`${this.mutedCount} muted`);
            return bits.join(' · ');
        },
    },
    methods: {
        patchPrefs(partial) {
            const next = normalizeMessageLogPrefs({ ...this.prefs, ...partial });
            this.$emit('update:prefs', next);
        },
        toggleCollapsed() {
            this.patchPrefs({ collapsed: !this.prefs.collapsed });
        },
        setSeverity(key, value) {
            this.patchPrefs({ [key]: value });
        },
        onTextFilter(value) {
            this.patchPrefs({ textFilter: value ?? '' });
        },
        muteFromDraft() {
            const next = addMutePattern(this.prefs, this.muteDraft);
            this.muteDraft = '';
            this.$emit('update:prefs', next);
        },
        muteTextFilter() {
            const next = addMutePattern(this.prefs, this.prefs.textFilter);
            this.$emit('update:prefs', { ...next, textFilter: '' });
        },
        muteMessage(text) {
            this.$emit('update:prefs', addMutePattern(this.prefs, suggestMutePattern(text)));
        },
        unmute(pattern) {
            this.$emit('update:prefs', removeMutePattern(this.prefs, pattern));
        },
        clearMutes() {
            this.patchPrefs({ mutePatterns: [] });
        },
        dismiss(id) {
            this.$emit('dismiss', id);
        },
        clearAll() {
            this.$emit('clear');
        },
        clearVisible() {
            this.$emit('clearVisible', this.visibleMessages.map(m => m.id));
        },
        muteLabel(pattern) {
            return formatMutePatternLabel(pattern);
        },
        formatTime(at) {
            if (!at)
                return '';
            try {
                return new Date(at).toLocaleTimeString(undefined, { hour12: false });
            }
            catch {
                return '';
            }
        },
    },
    render() {
        if (!this.hasAnything)
            return null;

        const collapsed = !!this.prefs.collapsed;

        return (
            <div
                class={[
                    'flow-v2-message-log',
                    collapsed ? 'flow-v2-message-log--collapsed' : '',
                ].join(' ')}
                role="region"
                aria-label="Flow editor messages"
            >
                <div class="flow-v2-message-log__header">
                    <span class="flow-v2-message-log__title">{this.titleText}</span>
                    {this.metaText ? (
                        <span class="flow-v2-message-log__meta">{this.metaText}</span>
                    ) : null}
                    <QSpace />
                    <QBtn
                        flat dense round size="sm"
                        icon="mdi-filter-variant"
                        color={this.filtersOpen || this.prefs.textFilter || this.prefs.mutePatterns?.length
                            || !this.prefs.showInfo || !this.prefs.showWarning || !this.prefs.showError
                            ? 'primary' : undefined}
                        aria-label="Message filters"
                    >
                        <QTooltip>Filters</QTooltip>
                        <QMenu
                            modelValue={this.filtersOpen}
                            onUpdate:modelValue={v => { this.filtersOpen = v; }}
                            anchor="top right"
                            self="bottom right"
                            class="q-pa-none flow-v2-message-log__filter-menu"
                        >
                            <div class="flow-v2-message-log__filters" onClick={e => e.stopPropagation()}>
                                <div class="flow-v2-message-log__sev">
                                    <QCheckbox
                                        dense
                                        size="sm"
                                        modelValue={this.prefs.showInfo}
                                        onUpdate:modelValue={v => this.setSeverity('showInfo', v)}
                                        label="Info"
                                    />
                                    <QCheckbox
                                        dense
                                        size="sm"
                                        modelValue={this.prefs.showWarning}
                                        onUpdate:modelValue={v => this.setSeverity('showWarning', v)}
                                        label="Warning"
                                    />
                                    <QCheckbox
                                        dense
                                        size="sm"
                                        modelValue={this.prefs.showError}
                                        onUpdate:modelValue={v => this.setSeverity('showError', v)}
                                        label="Error"
                                    />
                                </div>
                                <QInput
                                    dense outlined
                                    modelValue={this.prefs.textFilter}
                                    onUpdate:modelValue={v => this.onTextFilter(v)}
                                    label="Contains"
                                    clearable
                                    hideBottomSpace
                                    vSlots={{
                                        append: () => this.prefs.textFilter?.trim() ? (
                                            <QBtn
                                                flat dense
                                                label="Mute"
                                                onClick={this.muteTextFilter}
                                            />
                                        ) : null,
                                    }}
                                />
                                <QSeparator />
                                <div style={{ fontSize: '11px', color: '#607d8b' }}>
                                    Mute patterns (skip new matching messages)
                                </div>
                                <div class="flow-v2-message-log__mutes">
                                    {(this.prefs.mutePatterns || []).map(p => (
                                        <div key={p} class="flow-v2-message-log__mute-row" title={p}>
                                            <span class="flow-v2-message-log__mute-text">{this.muteLabel(p)}</span>
                                            <QBtn
                                                flat dense round size="xs"
                                                icon="mdi-close"
                                                aria-label={`Remove mute ${p}`}
                                                onClick={() => this.unmute(p)}
                                            />
                                        </div>
                                    ))}
                                    {!(this.prefs.mutePatterns || []).length ? (
                                        <span style={{ fontSize: '11px', color: '#90a4ae' }}>None</span>
                                    ) : null}
                                </div>
                                <div class="flow-v2-message-log__mute-add">
                                    <QInput
                                        dense outlined
                                        modelValue={this.muteDraft}
                                        onUpdate:modelValue={v => { this.muteDraft = v; }}
                                        placeholder="Add mute substring"
                                        hideBottomSpace
                                        onKeyup={e => { if (e.key === 'Enter') this.muteFromDraft(); }}
                                    />
                                    <QBtn flat dense label="Add" disable={!this.muteDraft.trim()} onClick={this.muteFromDraft} />
                                </div>
                                {(this.prefs.mutePatterns || []).length ? (
                                    <QBtn flat dense size="sm" label="Clear mutes" onClick={this.clearMutes} />
                                ) : null}
                            </div>
                        </QMenu>
                    </QBtn>
                    <QBtn
                        flat dense round size="sm"
                        icon="mdi-notification-clear-all"
                        disable={!this.messages?.length}
                        onClick={this.clearAll}
                        aria-label="Clear all messages"
                    >
                        <QTooltip>Clear all</QTooltip>
                    </QBtn>
                    {this.hiddenByFilter > 0 && this.visibleMessages.length > 0 ? (
                        <QBtn
                            flat dense round size="sm"
                            icon="mdi-filter-remove-outline"
                            onClick={this.clearVisible}
                            aria-label="Clear visible messages"
                        >
                            <QTooltip>Clear visible only</QTooltip>
                        </QBtn>
                    ) : null}
                    <QBtn
                        flat dense round size="sm"
                        icon={collapsed ? 'mdi-chevron-up' : 'mdi-chevron-down'}
                        onClick={this.toggleCollapsed}
                        aria-label={collapsed ? 'Expand messages' : 'Collapse messages'}
                    >
                        <QTooltip>{collapsed ? 'Expand' : 'Collapse'}</QTooltip>
                    </QBtn>
                </div>

                {!collapsed ? (
                    <div class="flow-v2-message-log__body">
                        {this.visibleMessages.length === 0 ? (
                            <div class="flow-v2-message-log__empty">
                                {this.messages?.length
                                    ? 'No messages match the current filters.'
                                    : 'All new matching messages are muted.'}
                            </div>
                        ) : this.visibleMessages.map(msg => (
                            <div
                                key={msg.id}
                                class={[
                                    'flow-v2-message-log__row',
                                    `flow-v2-message-log__row--${msg.color || 'info'}`,
                                ].join(' ')}
                            >
                                <span style={{ fontSize: '10px', opacity: 0.7, flexShrink: 0 }}>
                                    {this.formatTime(msg.at)}
                                </span>
                                <span style={{ fontSize: '10px', opacity: 0.7, flexShrink: 0, minWidth: '36px' }}>
                                    {SEVERITY_LABEL[msg.color] || 'Info'}
                                </span>
                                <span class="flow-v2-message-log__row-text">{msg.text}</span>
                                <div class="flow-v2-message-log__row-actions">
                                    <QBtn
                                        flat dense round size="xs"
                                        icon="mdi-volume-off"
                                        onClick={() => this.muteMessage(msg.text)}
                                        aria-label="Mute messages like this"
                                    >
                                        <QTooltip>Mute messages containing this text</QTooltip>
                                    </QBtn>
                                    <QBtn
                                        flat dense round size="xs"
                                        icon="mdi-close"
                                        onClick={() => this.dismiss(msg.id)}
                                        aria-label="Dismiss"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        );
    },
});
